import type { PostHog } from 'posthog-js'
import config from 'src/config'
import { isAutomatedTrafficUA } from 'src/utils/automatedTraffic'
import {
  redactTokenUrls,
  shouldSuppressCapabilityTelemetry,
  touchesRedactedToken
} from 'src/utils/capabilityPrivacy'
import {
  isKnownClientErrorNoise,
  isStaleChunkError
} from 'src/utils/clientErrorNoise'
import { classifyAttribution } from 'src/utils/marketing/classifyAttribution'
import { safeStorageGet, safeStorageSet } from 'src/utils/safeStorage'
import { handleStaleChunkError } from 'src/utils/staleChunkReload'
import { getExternalReferrer, getUTMsForAnalytics } from 'src/utils/utms'
import { isInternalTraffic } from './internal'

/**
 * Compute classified attribution super-properties from cookies + referrer.
 *
 * Mirrors the work `<UTMs />` does in its useEffect, but designed to run
 * synchronously from inside `posthog.init()`'s `loaded` callback so the
 * first autocaptured `$pageview` of every session carries
 * `classified_source` / `classified_medium`.
 *
 * Returns `null` on SSR or when reading cookies throws — safe to pass into
 * `posthog.register()` only when non-null.
 *
 * Why this duplicates `<UTMs />`'s register call: PostHog's `loaded`
 * callback runs INSIDE `init()` BEFORE the initial autocaptured
 * `$pageview` fires. `<UTMs />`'s register call is queued earlier (no
 * PostHog instance yet) and drains AFTER init returns — too late for the
 * first pageview. Registering here AND in `<UTMs />` is idempotent;
 * `<UTMs />`'s register still matters for SPA navigations where
 * `posthog.init()` doesn't re-run but the URL/referrer changed.
 */
const computeClassifiedSuperProperties = (): {
  classified_source: string
  classified_medium: string
} | null => {
  if (typeof window === 'undefined') return null
  try {
    const utms = getUTMsForAnalytics()
    const referrer = utms.referrer || getExternalReferrer()
    const classified = classifyAttribution({
      utmSource: utms.utm_source,
      utmMedium: utms.utm_medium,
      utmCampaign: utms.utm_campaign,
      utmTerm: utms.utm_term,
      utmContent: utms.utm_content,
      utmPage: utms.utm_page,
      utmReferrer: referrer,
      gclid: utms.gclid,
      fbclid: utms.fbclid,
      msclkid: utms.msclkid
    })
    return {
      classified_source: classified.utmSource ?? '(direct)',
      classified_medium: classified.utmMedium ?? '(none)'
    }
  } catch {
    // Don't crash PostHog init on a bad cookie read. The fallback path
    // is `<UTMs />`'s register call after drainQueue — that's still
    // intact, the loaded callback is only an optimization for event #1.
    return null
  }
}

/**
 * PostHog — product analytics + session replay + feature flags.
 *
 * Replaces the previous Microsoft Clarity integration. PostHog covers
 * Clarity's session-replay use case AND adds full event analytics + an
 * MCP server we can query from coding agents, which is the main reason
 * we switched.
 *
 * Performance: `posthog-js` is ~50 kB gzipped. We avoid bundling it on
 * the critical path by dynamic-importing the module on first call to
 * `init()` — `Scripts.tsx` triggers init from inside the same
 * `useDelayedLoad(2000)` fuse that fires the Facebook pixel, so the
 * library only downloads after user intent or 2s idle. Pure bouncers
 * never pay the cost.
 *
 * Pre-init queue: any call to `event` / `pageview` / `identifyUser` /
 * `setConsent` BEFORE the dynamic import resolves is buffered into a
 * bounded in-memory queue and drained in order once the real PostHog
 * client is ready. This matters because listing-page `view_item` and
 * other early conversion events fire within ~300 ms of hydration —
 * faster than the 2 s fuse + ~50 kB import. Without the queue those
 * events silently no-op. Queue is capped at 100 calls to bound memory
 * for pathological cases.
 *
 * Config:
 *   - `person_profiles: 'identified_only'` keeps anonymous-traffic cost
 *     down (PostHog bills per identified person profile). Anonymous
 *     events still capture, they just don't create a person row.
 *   - `defaults: '2026-01-30'` opts into the latest defaults bundle
 *     (autocapture, session recording on, exception capture, etc.) —
 *     the same flag PostHog's onboarding snippet ships with today.
 */

declare global {
  interface Window {
    posthog?: PostHog
  }
}

const POSTHOG_HOST = 'https://us.i.posthog.com'
const MAX_QUEUE = 100

type PendingCall = (ph: PostHog) => void

let phInstance: PostHog | null = null
let initPromise: Promise<void> | null = null
const queue: PendingCall[] = []

const getPosthog = (): PostHog | undefined => {
  if (isInternalTraffic()) return undefined
  return phInstance ?? undefined
}

const drainQueue = (ph: PostHog): void => {
  while (queue.length > 0) {
    const call = queue.shift()
    if (!call) continue
    try {
      call(ph)
    } catch {
      // Swallow — one bad call shouldn't poison the queue.
    }
  }
}

/**
 * Run `fn` against the live PostHog client if it's ready, otherwise
 * buffer it until `init()` resolves. Drops calls (silently) once the
 * queue is at capacity to keep memory bounded — at MAX_QUEUE=100 this
 * only ever matters in pathological cases (bot loops, etc.).
 */
const runOrQueue = (fn: PendingCall): void => {
  if (isInternalTraffic()) return
  if (phInstance) {
    try {
      fn(phInstance)
    } catch {
      // Swallow — one bad call shouldn't crash the caller.
    }
    return
  }
  if (queue.length < MAX_QUEUE) queue.push(fn)
}

/**
 * Is this `$exception` event a known browser/third-party artifact rather than
 * a SpotMe fault?
 *
 * `capture_exceptions: true` autocaptures every unhandled error and promise
 * rejection, which means PostHog Error Tracking sees the same adblocker
 * casualties, stale-deploy chunk failures, aborted fetches and Google Maps
 * self-inflicted throws that `loggerClient.ts` has been filtering out of
 * Datadog for months. Until this existed the two sinks disagreed, and the
 * unfiltered one produced a wrong read: on 2026-09-06 `UET is not defined` was
 * reported off PostHog as "the Bing pixel is broken on our busiest paid
 * lander" when it was one user running a content blocker against an
 * already-guarded snippet. Same rules, both sinks — see `clientErrorNoise.ts`.
 *
 * Chunk errors share one idempotent reload guard with Datadog
 * (`staleChunkReload.ts`), so whichever sink sees the error first triggers the
 * single reload and both agree on whether to log it.
 */
const isNoisyException = (properties: Record<string, unknown>): boolean => {
  // posthog-js (1.43x) sends ONLY `$exception_list` on the client — the flat
  // `$exception_types` / `$exception_values` arrays are derived at INGEST, so
  // they are absent when `before_send` runs. Reading just those two left
  // `kind` and `messages` empty and this whole filter matched almost nothing:
  // ChunkLoadError and "Script error." kept landing in PostHog Error Tracking
  // after 2026-09-06 (found 2026-09-21). Read the list; keep the flat arrays
  // as a fallback for older SDK payloads.
  const list = properties.$exception_list
  const entries = Array.isArray(list)
    ? (list as { type?: unknown; value?: unknown }[])
    : []
  const types = properties.$exception_types
  const values = properties.$exception_values
  const kind = entries.length
    ? String(entries[0]?.type ?? '')
    : Array.isArray(types)
      ? String(types[0] ?? '')
      : undefined
  const messages = entries.length
    ? entries.map((e) => String(e?.value ?? ''))
    : Array.isArray(values)
      ? values.map((v) => String(v))
      : []

  // PostHog models frames as structured objects; `isInjectedReferenceError`
  // reads Datadog's `  at <fn> @ <url>` text format. Synthesize that shape so
  // the RULE stays in one place and only the adapter differs. A frame with no
  // filename is an injected script — exactly what the predicate looks for.
  const frames = Array.isArray(list)
    ? list.flatMap((entry) => {
        const stacktrace = (entry as { stacktrace?: { frames?: unknown[] } })
          ?.stacktrace
        return Array.isArray(stacktrace?.frames) ? stacktrace.frames : []
      })
    : []
  const stackText = frames
    .map((frame) => {
      const { function: fn, filename } = (frame ?? {}) as {
        function?: string
        filename?: string
      }
      return `  at ${fn || '?'} @ ${filename || '<anonymous>'}`
    })
    .join('\n')

  const haystack = [kind, ...messages, stackText].filter(Boolean).join(' ')

  // Bot-driven React hydration errors. Strict renderers (Bingbot,
  // HeadlessChrome, Lighthouse) trip React 19's serialization compare on SSR'd
  // listing pages; real users don't at meaningful rates. Real-user #418 stays
  // visible — only automated UAs are dropped, matching `loggerClient.ts`.
  if (
    /Minified React error #(418|423|425)/.test(haystack) &&
    typeof navigator !== 'undefined' &&
    isAutomatedTrafficUA(navigator.userAgent)
  ) {
    return true
  }

  // Same verdict as Datadog (`staleChunkReload.ts`): drop while the one-shot
  // reload is in flight, keep it once the reload is spent and it still fails.
  if (isStaleChunkError(haystack))
    return handleStaleChunkError() === 'reloading'

  return isKnownClientErrorNoise({
    haystack,
    messages,
    error: { kind, stack: `${kind}: ${messages[0] ?? ''}\n${stackText}` }
  })
}

/**
 * Lazy-load posthog-js and initialize it. Safe to call multiple times —
 * subsequent calls return the in-flight (or already-resolved) promise.
 * Resolves once `posthog.init()` has run; any queued pre-init calls are
 * drained in order immediately after.
 */
/**
 * Android replay sampling (Jules, 2026-09-25). The recorder's full DOM
 * snapshot costs p75 359 ms / p90 614 ms of main thread on real Android
 * devices in MX (43 ms on iOS), and every later mutation batch p75 71 ms —
 * the size of the Android INP gap on its own (docs/performance/
 * interaction-latency.md → "Android INP is a shared-stack tax"). iOS and
 * desktop keep recording every session; Android records one in four.
 * Decided once per tab (sessionStorage) so a session is recorded whole or
 * not at all, never fragmented across page loads; an unsampled visitor also
 * never downloads the ~100 kB recorder script. Canvas capture is off at the
 * project level since the same day (the Maps WebGL canvas).
 *
 * Intent trigger (2026-11-03): an unsampled Android tab ALSO starts recording
 * the moment it turns interesting — a funnel page (`/reservar`, checkout,
 * KYC, login, add-listing, the domicilio wizard), a form start, a rage click,
 * a kept JS error, or its 3rd page. At one in four, most Android `/reservar`
 * and KYC abandoners had no recording (marketing/FUNNELS.md §1f), and those
 * are the sessions every funnel diagnosis needs. Adapted from RMH's
 * `tools/tags/posthog/init.js`. Such a recording opens mid-session by design,
 * then covers every later page of the tab (the stored '1'). Bounce traffic
 * (Flotillas, median 7 s) never reaches a trigger.
 */
const ANDROID_REPLAY_SAMPLE_RATE = 0.25
const REPLAY_SAMPLE_STORAGE_KEY = 'spotme:replay-sample'
const REPLAY_PAGEVIEW_COUNT_KEY = 'spotme:replay-pageviews'
const REPLAY_INTEREST_PAGEVIEWS = 3
const REPLAY_IDLE_START_TIMEOUT_MS = 3000
const REPLAY_INTEREST_PATH =
  /\/reservar\/?$|^\/(checkout|userConfirmation|complete-profile|login|authenticate|post)(\/|$)|\/almacenamiento-a-domicilio/
const REPLAY_INTEREST_EVENTS = new Set([
  '$rageclick',
  '$exception',
  'begin_checkout_preauth',
  'lead_form_step',
  'capture_lead_start',
  'storage_on_demand_lead_start'
])

interface IReplayTriggerEvent {
  event: string
  properties?: Record<string, unknown>
}

let isReplayRunning = false

const isAndroid = (): boolean => /android/i.test(navigator.userAgent)

const isReplayInterest = ({
  event,
  properties
}: IReplayTriggerEvent): boolean => {
  if (REPLAY_INTEREST_EVENTS.has(event)) return true
  if (event !== '$pageview') return false
  const pathname =
    typeof properties?.$pathname === 'string' ? properties.$pathname : ''
  if (REPLAY_INTEREST_PATH.test(pathname)) return true
  const views =
    Number(safeStorageGet('session', REPLAY_PAGEVIEW_COUNT_KEY) ?? 0) + 1
  safeStorageSet('session', REPLAY_PAGEVIEW_COUNT_KEY, String(views))
  return views >= REPLAY_INTEREST_PAGEVIEWS
}

/** Called from `before_send` on every kept event; see the intent note above. */
const maybeStartIntentReplay = (
  ph: PostHog,
  event: IReplayTriggerEvent
): void => {
  if (isReplayRunning || !isAndroid() || !isReplayInterest(event)) return
  safeStorageSet('session', REPLAY_SAMPLE_STORAGE_KEY, '1')
  if (touchesRedactedToken(window.location.href)) return
  isReplayRunning = true
  const start = (): void => {
    ph.startSessionRecording()
  }
  // Not on older Safari, though the DOM lib types it as always present.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(start, { timeout: REPLAY_IDLE_START_TIMEOUT_MS })
  } else {
    window.setTimeout(start, 0)
  }
}

const shouldRecordReplay = (): boolean => {
  if (!isAndroid()) return true
  const stored = safeStorageGet('session', REPLAY_SAMPLE_STORAGE_KEY)
  if (stored === '1') return true
  if (stored === '0') return false
  const record = Math.random() < ANDROID_REPLAY_SAMPLE_RATE
  safeStorageSet('session', REPLAY_SAMPLE_STORAGE_KEY, record ? '1' : '0')
  return record
}

const init = (): Promise<void> => {
  if (initPromise) return initPromise
  if (isInternalTraffic()) return Promise.resolve()
  if (typeof window === 'undefined') return Promise.resolve()
  if (!config.pixels.posthog) return Promise.resolve()

  initPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      if (shouldSuppressCapabilityTelemetry()) {
        queue.length = 0
        return
      }
      const isReplayDisabled =
        touchesRedactedToken(window.location.href) || !shouldRecordReplay()
      isReplayRunning = !isReplayDisabled
      posthog.init(config.pixels.posthog, {
        api_host: POSTHOG_HOST,
        defaults: '2026-01-30',
        // Initial pageview only. The defaults bundle turns on
        // 'history_change', which ALSO captured every SPA navigation at
        // pushState — before the new page rendered (old title), on top of
        // the one `ga.pageview()` already sends from routeChangeComplete:
        // two `$pageview`s per navigation from 2026-05-13 to 2026-09-23.
        // SPA pageviews come only from gtag.ts's mirror now.
        capture_pageview: true,
        person_profiles: 'identified_only',
        // Autocapture unhandled exceptions + promise rejections as
        // $exception events. Pairs with `enable_recording_console_log`
        // and session replay so we get: error message + stack + the
        // exact session showing what the user did before it fired.
        // Required for diagnosing React hydration errors (#418) which
        // throw into window.onerror without componentStack in prod.
        capture_exceptions: true,
        // Keep PostHog Error Tracking in step with Datadog's `beforeSend`.
        // Noise filtering is scoped to exceptions; capability privacy applies
        // to every event, including late pageviews/referrers after navigation.
        before_send: (event) => {
          if (shouldSuppressCapabilityTelemetry(event)) return null
          // Redacted token pages (invite, inventory, rosters): keep the event,
          // rewrite the token to `[token]` everywhere — `$current_url`,
          // `$referrer`, `$pathname`, `$session_entry_url`, `$set_once`
          // initial props, autocapture hrefs. Runs on EVERY page because the
          // session/initial props outlive the token page. Replay payloads are
          // skipped: recording is disabled on those pages (below).
          const redacted = redactTokenUrls(event, ['$snapshot_data'])
          const kept =
            redacted?.event === '$exception' &&
            isNoisyException(redacted.properties ?? {})
              ? null
              : redacted
          if (kept) maybeStartIntentReplay(posthog, kept)
          return kept
        },
        // Session replay records the full URL in its meta events — off on
        // any document whose URL carries a redacted token (the token page, or
        // login/KYC with it in `redirectA`/`redirectU`) rather than walking
        // every DOM snapshot.
        // …and on Android only one session in four, plus the tabs that turn
        // interesting — see shouldRecordReplay / maybeStartIntentReplay.
        disable_session_recording: isReplayDisabled,
        // No survey has ever been configured in this project (system.surveys
        // was empty on 2026-09-25), yet the SDK default lazy-loads surveys.js
        // on every page. Off until a survey exists — then delete this line.
        disable_surveys: true,
        // Capture console output inside session recordings — pairs the
        // visual replay with the runtime errors/warnings the user saw.
        // Particularly useful for debugging checkout/KYC drop-offs.
        enable_recording_console_log: true,
        // Core Web Vitals + network-request timing on every replay. We
        // already pay the network cost of the replay payload — adding
        // these lets us diagnose CWV regressions against the actual
        // session (not just an aggregate Looker number).
        capture_performance: {
          web_vitals: true,
          network_timing: true
        },
        session_recording: {
          // SpotMe forms collect Mexican PII at scale (CURP, RFC, full
          // legal name, address, phone, email, bank CLABE, payment
          // details). Default posthog-js leaves input values UNMASKED in
          // recordings — flip the default. This masks every <input> and
          // <textarea> value automatically; display-side PII (e.g. the
          // checkout review screen) should be opted-in to masking via a
          // `.ph-no-capture` class on the element.
          maskAllInputs: true,
          // Explicit even though false is the default — recording any
          // cross-origin iframe (Stripe Elements, Google Maps) would
          // both miss the content AND risk capturing third-party state.
          recordCrossOriginIframes: false
        },
        // CRITICAL: register UTM + classified attribution super-properties
        // INSIDE posthog's `loaded` callback. PostHog's internal `_loaded`
        // calls this hook BEFORE firing the autocaptured initial
        // `$pageview`, so super-properties are present on event #1 of
        // every session. Without this, `<UTMs />`'s register call queues
        // at hydration (no PostHog instance yet for the first ~2s while
        // posthog-js downloads) and drains AFTER init returns — leaving
        // the initial $pageview unclassified. That was the source of the
        // 49% of sessions previously landing in `(unset)` / `(direct)`
        // even though `<UTMs />` runs on every page (audit 2026-05-24).
        //
        // `<UTMs />`'s register call still runs on every SPA route change
        // (posthog.init() doesn't re-fire on client-side nav, but the
        // URL/referrer state can change), so this hook is the first-pass
        // and `<UTMs />` is the continuous-update path.
        loaded: (ph) => {
          const supers = computeClassifiedSuperProperties()
          if (supers) ph.register(supers)
        }
      })
      phInstance = posthog
      drainQueue(posthog)
    })
    .catch(() => {
      // Network/CSP failure — swallow silently. App keeps running with
      // GA4/Meta/UET still active. Clear the promise so a retry can be
      // attempted on a future interaction (e.g. consent change).
      initPromise = null
    })

  return initPromise
}

// Router events pass a path (`/search?city=…`); `$current_url` must be the
// absolute URL every other event carries.
const absoluteUrl = (url: string): string =>
  typeof window === 'undefined'
    ? url
    : new URL(url, window.location.href).toString()

const pageview = (url?: URL | string): void => {
  const $current_url =
    typeof url === 'string'
      ? absoluteUrl(url)
      : url instanceof URL
        ? url.toString()
        : undefined
  runOrQueue((ph) =>
    ph.capture('$pageview', $current_url ? { $current_url } : undefined)
  )
}

const event = (action: string, params?: Record<string, unknown>): void => {
  runOrQueue((ph) => ph.capture(action, params))
}

type IPosthogUser = {
  id?: string | null
  email?: string | null
  phone?: string | null
  fName?: string | null
  lName?: string | null
  // SpotMe-specific person properties. All optional — callers that only
  // know the contact details (e.g. form submissions before auth) just
  // pass the basics; the AuthProvider call adds the rich props once
  // currentUser resolves.
  customerType?: string | null
  hubspotId?: string | null
  hsOwnerId?: string | null
  isVerified?: boolean | null
  teamId?: string | null
  isAdmin?: boolean | null
}

const identifyUser = ({
  id,
  email,
  phone,
  fName,
  lName,
  customerType,
  hubspotId,
  hsOwnerId,
  isVerified,
  teamId,
  isAdmin
}: IPosthogUser): void => {
  const distinctId = id ?? email ?? phone
  if (!distinctId) return
  runOrQueue((ph) =>
    ph.identify(distinctId, {
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      ...(fName ? { first_name: fName } : {}),
      ...(lName ? { last_name: lName } : {}),
      ...(customerType ? { customer_type: customerType } : {}),
      ...(hubspotId ? { hubspot_id: hubspotId } : {}),
      ...(hsOwnerId ? { hs_owner_id: hsOwnerId } : {}),
      ...(typeof isVerified === 'boolean' ? { is_verified: isVerified } : {}),
      ...(teamId ? { team_id: teamId } : {}),
      ...(typeof isAdmin === 'boolean' ? { is_admin: isAdmin } : {})
    })
  )
}

/**
 * Associate the current person with a PostHog group. Used to attach
 * B2B users to their Team so PostHog can compute team-level retention,
 * multi-listing behavior, and group-scoped funnels — the kind of
 * analytics GA4 / Looker cannot do because they have no concept of a
 * customer hierarchy.
 *
 * SpotMe uses one group type: `team`. Called from AuthProvider when
 * `currentUser.teamId` is present.
 */
const setGroup = (
  groupType: string,
  groupKey: string,
  properties?: Record<string, string | number | boolean>
): void => {
  if (!groupType || !groupKey) return
  runOrQueue((ph) => ph.group(groupType, groupKey, properties))
}

/**
 * Honor the cookie consent banner — Clarity used to be the only tool
 * with an explicit consent toggle; PostHog's equivalent is
 * `opt_in_capturing` / `opt_out_capturing`. Called from `CookieConsent`
 * once the user makes a choice.
 */
const setConsent = (analyticsAllowed: boolean): void => {
  runOrQueue((ph) => {
    if (analyticsAllowed) {
      ph.opt_in_capturing()
    } else {
      ph.opt_out_capturing()
    }
  })
}

/**
 * Tag the current person as internal SpotMe traffic so the project's
 * `test_account_filters` rule (`$is_internal_user exact true`) can
 * exclude them from product-analytics dashboards. Called from
 * `AuthProvider` when `currentUser.isAdmin === true`. Idempotent.
 *
 * Sets a regular person property — PostHog's built-in internal-user
 * filter reads `$is_internal_user` off the person, so this is enough
 * (no need for the deprecated `set_internal_or_test_user` helper which
 * isn't typed on the modern `PostHog` interface).
 */
const markInternalUser = (): void => {
  runOrQueue((ph) => ph.setPersonProperties({ $is_internal_user: true }))
}

/**
 * Set persistent super-properties that ride on every subsequent event
 * (autocapture + custom). Used by `<UTMs />` to push SpotMe's richer
 * attribution (sticky-cookie UTMs, HubSpot Ads `hsa_*` derivation,
 * `gclid`/`fbclid`/`msclkid`) into PostHog alongside its native
 * URL-time `$utm_*` capture — so cohorts and funnels in PostHog match
 * the server-side marketing report 1:1.
 *
 * No `$` prefix on the keys so we never collide with PostHog's native
 * `$utm_*` autocapture properties; both end up on each event as
 * distinct fields and can be queried independently.
 */
const register = (
  properties: Record<string, string | number | boolean>
): void => {
  runOrQueue((ph) => ph.register(properties))
}

/**
 * Read a feature flag value. Returns `undefined` until PostHog has loaded
 * AND flags have resolved (the SDK pulls `/decide` on init). Callers that
 * need to render before flags resolve should treat `undefined` as "show
 * control" — the `useFeatureFlag` hook below does that automatically.
 *
 * Multi-variant flags return the variant key as a string. Boolean flags
 * return `true` / `false`. Disabled flags return `false`.
 */
const featureFlag = (key: string): boolean | string | undefined => {
  if (isInternalTraffic()) return undefined
  if (!phInstance) return undefined
  return phInstance.getFeatureFlag(key)
}

/**
 * Read the current PostHog session ID synchronously. Returns `''` when
 * PostHog hasn't initialized yet (first paint, pre-fuse). The ID is
 * stable across page navigations within a 30-min idle window.
 *
 * Used by the WhatsApp lead-form flow to embed a hashed token in the
 * outbound prefill so the backend can correlate the inbound message
 * back to the originating PostHog session — closing the
 * `qualified_whatsapp_lead` loop.
 */
const sessionId = (): string => {
  if (isInternalTraffic()) return ''
  if (!phInstance) return ''
  try {
    return phInstance.get_session_id() ?? ''
  } catch {
    return ''
  }
}

/**
 * Link `aliasId` to the current person so PostHog merges both identifiers'
 * event histories into one profile. Thin wrapper over `posthog.alias` —
 * callers own their own dedupe (see `aliasWaSession`).
 *
 * `ph.alias(alias)` with no 2nd arg attaches `aliasId` to the CURRENT
 * distinct_id. Two constraints from PostHog's docs that the WhatsApp
 * caller is built around:
 *
 *   1. `aliasId` must never have been the distinct_id of a prior
 *      `identify`/`alias` call, or PostHog refuses the merge. Ours
 *      (`session:{token}`) is only ever a plain server-side `capture`
 *      distinct_id, and the browser always aliases BEFORE the server
 *      fires, so the ordering holds.
 *   2. Under `person_profiles: 'identified_only'` (our config), `alias`
 *      is an IDENTIFYING call — it creates a person profile and every
 *      subsequent event for that person is billed as identified. So only
 *      alias populations we deliberately want profiles for; never on a
 *      broad pageview path.
 */
const alias = (aliasId: string): void => {
  if (!aliasId) return
  runOrQueue((ph) => ph.alias(aliasId))
}

/**
 * Run `cb` once the PostHog client is live — immediately if it already is,
 * otherwise buffered on the same pre-init queue every other call uses and
 * drained when `init()` resolves.
 *
 * Exists because `sessionId()` returns `''` until the lazy `posthog-js`
 * import resolves behind the 2 s `useDelayedLoad` fuse in `Scripts.tsx`.
 * Anything that needs the session id at RENDER time (the WhatsApp prefill
 * token) would otherwise read `''` once at first paint and never recover —
 * see `useWaSessionToken`. Never fires when PostHog doesn't initialize at
 * all (no key, internal traffic, SSR), which is the correct degradation.
 */
const onReady = (cb: () => void): void => {
  runOrQueue(() => cb())
}

/**
 * Manually capture an exception as a PostHog `$exception` event.
 *
 * PostHog's `capture_exceptions: true` autocapture listens for
 * `window.onerror` / `unhandledrejection`, which React 19's
 * `defaultOnRecoverableError` path does NOT reliably hit in prod —
 * recoverable hydration errors (#418/#419/#421/#422/#423/#425) call
 * `reportError(error)` which the browser may not surface through the
 * same window-level handler chain PostHog wraps. Result: hydration
 * mismatches don't show up in PostHog Error Tracking unless we
 * explicitly capture them.
 *
 * Called from `_app.tsx#reportHydration` once a #418 is confirmed via
 * either the wrapped `console.error` path or the `window.error`
 * listener. Properties match the Datadog `hydration_mismatch` payload
 * so the two systems agree on the same fingerprint.
 *
 * Safe before init — calls queue via `runOrQueue` and drain on
 * `init()` resolve, so #418s that fire during hydration (well before
 * the 2 s PostHog fuse) are buffered, not lost.
 */
const captureException = (
  error: unknown,
  additionalProperties?: Record<string, unknown>
): void => {
  runOrQueue((ph) => {
    if (error instanceof Error) {
      ph.captureException(error, additionalProperties)
    } else {
      ph.captureException(new Error(String(error)), additionalProperties)
    }
  })
}

/**
 * Subscribe to flag resolution. The callback fires the moment PostHog's
 * `/decide` response lands (and every time flags re-evaluate — e.g. after
 * `identify`). Returns an unsubscribe function. Safe to call before
 * `init()`; the subscription queues and attaches on init.
 */
const onFeatureFlags = (callback: () => void): (() => void) => {
  if (isInternalTraffic()) return () => undefined
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  runOrQueue((ph) => {
    if (cancelled) return
    unsubscribe = ph.onFeatureFlags(() => callback())
  })

  return () => {
    cancelled = true
    if (unsubscribe) unsubscribe()
  }
}

const posthog = {
  init,
  getPosthog,
  pageview,
  event,
  identifyUser,
  setGroup,
  setConsent,
  markInternalUser,
  register,
  featureFlag,
  onFeatureFlags,
  sessionId,
  alias,
  onReady,
  captureException
}

export default posthog
